import React, { useState, useEffect } from "react";
import { get, put } from "../../../services/api";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

function ApplicantList() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const organizationId = localStorage.getItem("organizationId");
  const [applicants, setApplicants] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  // Status options based on ApplicantStatus enum
  const statusOptions = [
    "New",
    "Shortlisted",
    "InterviewScheduled",
    "Selected",
    "Rejected",
  ];
  const fetchApplicants = async () => {
    try {
      setLoading(true);
      const response = await get(
        `Onboarding/applicants?CenterId=${savedCenterId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.success) {
        setApplicants(response.data || []);
        toast.success(`Loaded ${response.data?.length || 0} applicants`);
      }
    } catch (error) {
      console.error("Error fetching applicants:", error);
      toast.error("Failed to fetch applicants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicants();
  }, []);

  // Filter applicants
  const filteredApplicants = applicants.filter((applicant) => {
    const matchesSearch =
      applicant.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      applicant.positionTitle
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      applicant.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      applicant.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  // Handle status change
  const handleStatusChange = async (applicantId, newStatus) => {
    try {
      const response = await put(
        `Onboarding/applicants/${applicantId}/status`,
        {
          status: newStatus,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.success) {
        toast.success("Status updated successfully");
        fetchApplicants(); // Refresh
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  // Handle create employee
  const handleCreateEmployee = (applicant) => {
    if (applicant.status !== "Selected") {
      const confirmed = window.confirm(
        `Applicant status is "${applicant.status}".\nRecommended status is "Selected" before creating employee.\nCreate employee anyway?`,
      );
      if (!confirmed) return;
    }

    // Navigate to employee profile with pre-filled data
    navigate(
      `/employee?mode=new&name=${encodeURIComponent(applicant.fullName)}&email=${encodeURIComponent(
        applicant.email,
      )}&phone=${encodeURIComponent(applicant.phone)}`,
    );
    toast.info(`Creating employee profile for ${applicant.fullName}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          All Applicant List
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage all applicants in the recruitment pipeline (
          {filteredApplicants.length} found)
        </Typography>
      </Box>

      <Card>
        <CardContent>
          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search applicant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 220 }}
              />

              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button variant="outlined" size="small" onClick={fetchApplicants}>
                <i className="fas fa-sync me-1"></i>
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Applicants Table */}
          {filteredApplicants.length === 0 ? (
            <Alert severity="info">
              No applicants found. Try adjusting your filters.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                    <TableCell>#</TableCell>
                    <TableCell>Applicant Name</TableCell>
                    <TableCell>Applied For</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell align="right">Expected Salary</TableCell>
                    <TableCell width={190}>Status</TableCell>
                    <TableCell align="right" width={170}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredApplicants.map((applicant, index) => (
                    <TableRow key={applicant.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{applicant.fullName}</TableCell>
                      <TableCell>{applicant.positionTitle || "N/A"}</TableCell>
                      <TableCell>{applicant.phone}</TableCell>
                      <TableCell>{applicant.email}</TableCell>
                      <TableCell>{applicant.city || "N/A"}</TableCell>
                      <TableCell align="right">
                        {applicant.expectedSalary
                          ? `Rs. ${applicant.expectedSalary.toLocaleString()}`
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={applicant.status || "Applied"}
                            onChange={(e) =>
                              handleStatusChange(applicant.id, e.target.value)
                            }
                          >
                            {statusOptions.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PersonAddIcon />}
                          onClick={() => handleCreateEmployee(applicant)}
                        >
                          Create Employee
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Tip:</strong> Set status to <strong>Selected</strong> then
              click <strong>Create Employee</strong> to convert applicant to
              employee.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ApplicantList;
